import { useState, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

const useConversation = (conversationId) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState(null);
  const onResponseCompleteRef = useRef(null);

  const setOnResponseComplete = (callback) => {
    onResponseCompleteRef.current = callback;
  };

  const loadMessages = async () => {
    if (!conversationId) return;
    try {
      const response = await fetch(`${API}/conversations/${conversationId}`, {
        headers: authHeaders(),
      });
      const data = await response.json();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const sendMessage = async (content) => {
    if (!content.trim() || !conversationId) return;

    setIsLoading(true);

    const userMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: conversationId,
      role: 'user',
      content: content,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);

    setStreamingMessage({
      id: `streaming-${Date.now()}`,
      conversation_id: conversationId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await fetch(`${API}/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ content }),
      });

      if (!response.ok) throw new Error('Failed to send message');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);

            if (data === '[DONE]') {
              const trimmed = fullContent.trim();
              const finalMessage = {
                id: `msg-${Date.now()}`,
                conversation_id: conversationId,
                role: 'assistant',
                content: trimmed,
                timestamp: new Date().toISOString(),
              };

              setMessages(prev => [...prev, finalMessage]);
              setStreamingMessage(null);

              // Fire complete callback with FULL text after delay
              if (onResponseCompleteRef.current && trimmed) {
                setTimeout(() => {
                  if (!onResponseCompleteRef.current) return;
                  onResponseCompleteRef.current(trimmed);
                }, 500);
              }
              break;
            }

            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                fullContent += parsed.content;
                setStreamingMessage(prev => ({ ...prev, content: fullContent }));
              }
            } catch (e) {
              // Ignore parsing errors for incomplete chunks
            }
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setStreamingMessage(null);

      const errorMessage = {
        id: `error-${Date.now()}`,
        conversation_id: conversationId,
        role: 'assistant',
        content: 'I apologize... there seems to be a disturbance in our connection. Can we try again?',
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    messages,
    isLoading,
    streamingMessage,
    sendMessage,
    loadMessages,
    setOnResponseComplete,
  };
};

export default useConversation;
